import { useMemo } from "react";
import { type UserStory } from "@/lib/projectStore";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Layers } from "lucide-react";

interface Props {
  stories: UserStory[];
  projectStartDate: string;
  sprintLengthWeeks: number;
}

export default function CumulativeFlowDiagram({ stories, projectStartDate, sprintLengthWeeks }: Props) {
  const chartData = useMemo(() => {
    const startMs = new Date(projectStartDate).getTime();
    const sprintMs = sprintLengthWeeks * 7 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const totalSprints = Math.min(Math.max(1, Math.ceil((now - startMs) / sprintMs)), 12);

    const data: { sprint: string; done: number; inProgress: number; backlog: number }[] = [];

    for (let i = 0; i <= totalSprints; i++) {
      const cutoff = startMs + i * sprintMs;
      let done = 0;
      let inProgress = 0;
      let backlog = 0;

      stories.forEach((s) => {
        const pts = s.storyPoints || 0;
        const dueMs = s.dueDate ? new Date(s.dueDate).getTime() : null;
        const startDateMs = s.startDate ? new Date(s.startDate).getTime() : null;

        // Determine status at this point in time
        if (s.status === "done" && dueMs && dueMs <= cutoff) {
          done += pts;
        } else if (
          (s.status === "in-progress" || s.status === "done") &&
          startDateMs && startDateMs <= cutoff &&
          !(s.status === "done" && dueMs && dueMs <= cutoff)
        ) {
          inProgress += pts;
        } else {
          backlog += pts;
        }
      });

      data.push({
        sprint: i === 0 ? "Start" : `S${i}`,
        done,
        inProgress,
        backlog,
      });
    }

    return data;
  }, [stories, projectStartDate, sprintLengthWeeks]);

  const hasData = stories.length > 0 && stories.some((s) => (s.storyPoints || 0) > 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-display flex items-center gap-2">
          <Layers className="h-4 w-4 text-accent" />
          Cumulative Flow
        </CardTitle>
        <CardDescription className="text-xs">
          Story points by status over time — stacked area shows flow balance
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
            <div className="text-center space-y-2">
              <Layers className="h-8 w-8 mx-auto opacity-40" />
              <p>No stories with points yet.</p>
              <p className="text-xs">Add story points to see the cumulative flow diagram.</p>
            </div>
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                <defs>
                  <linearGradient id="cfdDone" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-2))" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0.1} />
                  </linearGradient>
                  <linearGradient id="cfdProgress" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-4))" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="hsl(var(--chart-4))" stopOpacity={0.1} />
                  </linearGradient>
                  <linearGradient id="cfdBacklog" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" />
                <XAxis
                  dataKey="sprint"
                  tick={{ fontSize: 11 }}
                  className="fill-muted-foreground"
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  className="fill-muted-foreground"
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                  formatter={(value: number, name: string) => {
                    const labels: Record<string, string> = { done: "Done", inProgress: "In Progress", backlog: "Backlog" };
                    return [`${value} pts`, labels[name] || name];
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="done"
                  stackId="1"
                  stroke="hsl(var(--chart-2))"
                  fill="url(#cfdDone)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="inProgress"
                  stackId="1"
                  stroke="hsl(var(--chart-4))"
                  fill="url(#cfdProgress)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="backlog"
                  stackId="1"
                  stroke="hsl(var(--chart-1))"
                  fill="url(#cfdBacklog)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
